import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { billingApi } from './api';
import { useAuth } from './AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Check, Loader2, Sparkles, Crown } from 'lucide-react';
import { toast } from 'sonner';

const FEATURES = {
  free: [
    '5 AI Coach messages/day',
    '2 Pre-Game plans/day',
    '3 Journal reflections/day',
    'No saved chat or plan history',
  ],
  pro: [
    '50 AI Coach messages/day (10x)',
    '15 Pre-Game plans/day',
    '25 Journal reflections/day',
    'Full chat & plan history saved',
    'Future features included (audio, video library, progress dashboard)',
    '7-day free trial — no card required',
  ],
  premium: [
    'Everything in Apex AI',
    'Unlimited AI usage',
    'Direct text & email access with Coach Lewis',
    '24-hour response time guaranteed',
    'Personalized check-ins to keep you on track',
    '7-day free trial — no card required',
  ],
};

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [interval, setInterval] = useState('month');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    Promise.all([billingApi.plans(), billingApi.subscription().catch(() => null)])
      .then(([p, s]) => {
        setPlans(p?.plans || []);
        setSub(s);
      })
      .finally(() => setLoading(false));
  }, []);

  const planFor = (tier) => plans.find((p) => p.tier === tier && p.interval === interval);
  const monthlyOf = (tier) => plans.find((p) => p.tier === tier && p.interval === 'month');

  const startCheckout = async (plan_key) => {
    if (!user) { navigate('/app/login'); return; }
    setSubmitting(plan_key);
    try {
      const { url } = await billingApi.checkout(plan_key);
      window.location.href = url;
    } catch (e) {
      toast.error(e.message || 'Could not start checkout. Stripe may not be configured yet.');
      setSubmitting(null);
    }
  };

  const openPortal = async () => {
    try {
      const { url } = await billingApi.portal();
      window.location.href = url;
    } catch (e) {
      toast.error(e.message || 'Could not open billing portal.');
    }
  };

  const currentTier = (sub?.tier) || 'free';

  const tiers = [
    {
      tier: 'free',
      name: 'Free',
      icon: null,
      tagline: 'Try the AI mental performance tools.',
      ctaLabel: 'Current plan',
      tone: 'light',
    },
    {
      tier: 'pro',
      name: 'Apex AI',
      icon: Sparkles,
      tagline: 'Unlimited AI mental performance training, anytime.',
      ctaLabel: 'Start 7-day free trial',
      tone: 'highlight',
    },
    {
      tier: 'premium',
      name: '1:1 with Coach Lewis',
      icon: Crown,
      tagline: 'Direct access to Coach Lewis. Real human guidance when you need it.',
      ctaLabel: 'Start 7-day free trial',
      tone: 'dark',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="pricing-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#004C54]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pricing-page">
      <div className="text-center max-w-[60ch] mx-auto">
        <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#004C54] font-semibold mb-2">Pricing</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Choose your plan</h1>
        <p className="text-[15px] text-foreground/70 mt-2">Free forever, or upgrade for higher limits and premium features. Cancel anytime.</p>
      </div>

      <Card className="border-[#004C54]/30 bg-[#004C54]/5 max-w-[64ch] mx-auto" data-testid="pricing-trial-banner">
        <CardContent className="p-4 sm:p-5 flex items-start sm:items-center gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#004C54] text-white flex items-center justify-center">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono-accent uppercase tracking-[0.18em] text-[10px] font-bold text-[#004C54]">7 days free</div>
            <div className="font-heading text-base sm:text-lg font-bold leading-snug mt-0.5">
              Try Apex AI Coach for a week — no card required.
            </div>
            <div className="text-sm text-foreground/70 mt-0.5 leading-snug">
              Full access. Cancel anytime. You'll only be charged if you add a card before the trial ends.
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-center">
        <Tabs value={interval} onValueChange={setInterval}>
          <TabsList data-testid="interval-tabs">
            <TabsTrigger value="month" data-testid="interval-month">Monthly</TabsTrigger>
            <TabsTrigger value="year" data-testid="interval-year">Yearly <span className="ml-1.5 text-[10px] bg-[#004C54] text-white rounded-full px-1.5 py-0.5">Save ~17%</span></TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 sm:gap-5">
        {tiers.map((t) => {
          const plan = planFor(t.tier);
          const monthly = monthlyOf(t.tier);
          const Icon = t.icon;
          const isCurrent = currentTier === t.tier;
          const isFree = t.tier === 'free';
          const dark = t.tone === 'dark';
          const highlight = t.tone === 'highlight';

          // Card styling derived from tier tone (no nested ternaries inline)
          let cardToneClass = 'border-border';
          if (dark) cardToneClass = 'bg-[#0a0d0e] border-[#0a0d0e] text-white';
          else if (highlight) cardToneClass = 'border-2 border-[#004C54] shadow-lg';

          let priceDisplay;
          let priceSuffix;
          if (isFree) {
            priceDisplay = '$0';
            priceSuffix = 'forever';
          } else if (plan) {
            if (interval === 'year' && monthly) {
              const monthlyEquivalent = (plan.amount / 12 / 100).toFixed(2);
              priceDisplay = `$${monthlyEquivalent}`;
              priceSuffix = `/mo, billed ${plan.amount_display}/yr`;
            } else {
              priceDisplay = plan.amount_display;
              priceSuffix = '/month';
            }
          }

          // Pick the right CTA button for this tier
          const renderCta = () => {
            if (isCurrent && isFree) {
              return (
                <Button variant="outline" disabled className="w-full h-11" data-testid={`cta-${t.tier}`}>
                  Current plan
                </Button>
              );
            }
            if (isCurrent) {
              return (
                <Button onClick={openPortal} variant="outline" className="w-full h-11" data-testid={`cta-${t.tier}`}>
                  Manage subscription
                </Button>
              );
            }
            if (isFree) {
              return (
                <Button variant="outline" onClick={() => navigate(user ? '/app/dashboard' : '/app/login')} className="w-full h-11" data-testid={`cta-${t.tier}`}>
                  {user ? 'Go to dashboard' : 'Get started free'}
                </Button>
              );
            }
            const buttonToneClass = dark ? 'bg-white text-[#0a0d0e] hover:bg-white/90' : 'bg-[#004C54] hover:bg-[#003a40] text-white';
            return (
              <Button
                onClick={() => startCheckout(plan?.plan_key)}
                disabled={!plan || submitting === plan?.plan_key}
                className={`w-full h-11 font-semibold ${buttonToneClass}`}
                data-testid={`cta-${t.tier}`}
              >
                {submitting === plan?.plan_key ? <Loader2 className="h-4 w-4 animate-spin" /> : t.ctaLabel}
              </Button>
            );
          };

          return (
            <Card
              key={t.tier}
              className={`relative ${cardToneClass}`}
              data-testid={`tier-${t.tier}`}
            >
              {highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-[#004C54] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">Most popular</span>
                </div>
              )}
              <CardContent className="p-6 sm:p-7 flex flex-col h-full">
                <div className="flex items-center gap-2 mb-3">
                  {Icon && <Icon className={`h-5 w-5 ${dark ? 'text-[#A5ACAF]' : 'text-[#004C54]'}`} />}
                  <h3 className={`font-heading text-2xl font-bold ${dark ? 'text-white' : 'text-foreground'}`}>{t.name}</h3>
                </div>
                <p className={`text-sm leading-relaxed mb-4 ${dark ? 'text-white/65' : 'text-foreground/70'}`}>{t.tagline}</p>
                <div className="mb-5">
                  <div className="flex items-baseline gap-1.5">
                    <span className={`text-4xl font-bold tracking-tight ${dark ? 'text-white' : 'text-foreground'}`}>{priceDisplay}</span>
                    <span className={`text-sm ${dark ? 'text-white/50' : 'text-foreground/60'}`}>{priceSuffix}</span>
                  </div>
                </div>
                <ul className="space-y-2 mb-6 flex-1">
                  {FEATURES[t.tier].map((f) => (
                    <li key={f} className={`flex items-start gap-2 text-sm ${dark ? 'text-white/80' : 'text-foreground/80'}`}>
                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${dark ? 'text-[#4FBCC7]' : 'text-[#004C54]'}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {renderCta()}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <p className="text-center text-xs text-foreground/50 max-w-[60ch] mx-auto">
        Subscriptions auto-renew. Cancel anytime from the customer portal. Trial converts to paid after 7 days unless cancelled. Prices in USD.
      </p>
    </div>
  );
}
